import React, { useState } from "react";
import { Button, Checkbox } from "antd";
import { Company } from "common/interfaces";
import { Link } from "react-router-dom";
import { UserTypeEnum, UserTypeToLabel } from "common/enums/UserType.enum";
import { companyInfoStatusMap } from "common/helper/text.helper";
import { CompanyInfoStatusEnum } from "common/enums/CompanyInfoStatus.enum";
import { useUpdateCompanyMutation } from "reducers/companyApi";
import CustomConfirmModal from "./CustomConfirmModal";

interface CompanyManageItemProps<T extends Company> {
  company: T;
  checked: boolean;
  onSelect: (code: string, checked: boolean) => void;
}

export function CompanyManageItem<T extends Company>({
  company,
  checked,
  onSelect,
}: CompanyManageItemProps<T>) {
  const [updateCompany] = useUpdateCompanyMutation();
  const [openConfirmModalFrozen, setOpenConfirmModalFrozen] = useState(false);
  const [companyCode, setCompanyCode] = useState("");

  const onUpdateCompany = async () => {
    await updateCompany({ companyCode, status: CompanyInfoStatusEnum.FROZEN });
    setOpenConfirmModalFrozen(false);
    setCompanyCode("");
  };
  const onFrozen = (code: string) => {
    setOpenConfirmModalFrozen(true);
    setCompanyCode(code);
  };

  return (
    <div className="self-stretch bg-white rounded-md flex flex-col justify-start items-start overflow-hidden">
      <div
        data-プロパティ1="base + checkbox"
        className="self-stretch min-h-10 px-12 py-2.5 relative bg-primary inline-flex justify-center items-center gap-2.5"
      >
        <div className="flex-1 text-center justify-start text-white text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
          申請ID #{company.code}
        </div>
        <div
          data-プロパティ1="active - border"
          className="w-7 h-7 py-[3px] left-[10px] top-[6px] absolute bg-primary rounded-md inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden"
        >
          <Checkbox
            checked={checked}
            onChange={(e) => onSelect(company.code, e.target.checked)}
          />
        </div>
      </div>
      <div className="self-stretch min-h-10 p-5 bg-white rounded-bl-md rounded-br-md border-l border-r border-b border-neutral-300 flex flex-col justify-center items-center gap-5">
        <div className="self-stretch inline-flex justify-start items-start gap-3.5 flex-wrap content-start">
          <div className="min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              法人名
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {company.company_name}
            </div>
          </div>
          <div className="min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              担当者名
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {company.contact_last_name}
            </div>
          </div>
          <div className="min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              メールアドレス
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {company.email}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              ユーザー種別
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {UserTypeToLabel[company.type as UserTypeEnum]}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              ステータス
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {companyInfoStatusMap[company.status]}
            </div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${company.code}/view`}
          className="w-full"
        >
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full min-h-[50px] bg-primary-light text-primary text-[16px] font-bold"
          >
            詳細画面へ
          </Button>
        </Link>
        <div
          className="text-primary text-[14px] font-bold"
          onClick={() => onFrozen(company.code)}
        >
          解除する
        </div>
      </div>
      <CustomConfirmModal
        open={openConfirmModalFrozen}
        title="このユーザーを
          凍結してもよろしいですか？"
        description={`このユーザーのアカウントを凍結すると、
          ログイン・出品・購入・メッセージなど、
          全ての機能が停止します。\n`}
        note={`
          ※凍結状態は解除されるまで自動で復帰しません。
          ※現在の取引には影響する可能性があります。`}
        onOk={onUpdateCompany}
        onCancel={() => setOpenConfirmModalFrozen(false)}
        okText="ユーザーを凍結する"
        cancelText="戻る"
        okButtonBgColor="bg-red-dark"
      />
    </div>
  );
}

export default CompanyManageItem;
